import { useEffect, useState } from "react";
import { Plus, Trash2, Umbrella, GraduationCap } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { fmtUK, todayISO } from "../../lib/utils";
import CalendarView from "../../components/CalendarView";

export default function AdminCalendar() {
  const [holidays, setHolidays] = useState([]);
  const [exams, setExams] = useState([]);
  const [yearGroups, setYearGroups] = useState([]);
  const [weeks, setWeeks] = useState(1);
  const [form, setForm] = useState({ name: "", start: "", end: "", kind: "closure" });
  const [examForm, setExamForm] = useState({ title: "", date: "", start_time: "", year_group: "all", notes: "" });
  const [busy, setBusy] = useState(false);
  const [examPast, setExamPast] = useState(false);

  const load = () => {
    api.get("/school/holidays").then((r) => setHolidays(r.data)).catch((e) => toast.error(apiError(e)));
    api.get("/school/exams").then((r) => setExams(r.data)).catch(() => {});
    api.get("/school/schedule").then((r) => setWeeks(r.data.weeks || 1)).catch(() => {});
    api.get("/school/profile").then((r) => setYearGroups(r.data.year_groups || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/school/holidays", form);
      toast.success("Holiday added");
      setForm({ name: "", start: "", end: "", kind: "closure" });
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/school/holidays/${id}`);
      toast.success("Holiday removed");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const addExam = async (e) => {
    e.preventDefault();
    if (examForm.date && examForm.date < todayISO() && !examPast) {
      setExamPast(true);
      return;
    }
    try {
      await api.post("/school/exams", examForm);
      toast.success("Exam added");
      setExamForm({ title: "", date: "", start_time: "", year_group: "all", notes: "" });
      setExamPast(false);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const removeExam = async (id) => {
    try {
      await api.delete(`/school/exams/${id}`);
      toast.success("Exam removed");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="admin-calendar-page">
      <span className="nb-label">Calendar</span>
      <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mt-2">Holidays, study days & exams</h1>
      <p className="text-zinc-500 mt-2 max-w-xl">Add closures and study days off in advance — summer, Easter, Christmas, inset days, exam study leave. They show on every pupil's calendar and teachers' homework due-dates skip them automatically.</p>

      <div className="mt-8 grid lg:grid-cols-5 gap-6 items-start">
        <div className="lg:col-span-2 space-y-4">
          <div className="nb-card p-6">
            <h2 className="font-display font-bold text-lg flex items-center gap-2"><Umbrella className="w-5 h-5" /> Add closure</h2>
            <form onSubmit={add} className="mt-4 space-y-3">
              <div>
                <label className="nb-label" htmlFor="hol-name">Name</label>
                <input id="hol-name" className="nb-input mt-2" data-testid="holiday-name-input" placeholder="e.g. Summer holidays"
                  value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required maxLength={60} />
              </div>
              <div>
                <label className="nb-label" htmlFor="hol-kind">Type</label>
                <select id="hol-kind" className="nb-input mt-2" data-testid="holiday-kind-select"
                  value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
                  <option value="closure">School closed</option>
                  <option value="study">Study day off</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="nb-label" htmlFor="hol-start">First day closed</label>
                  <input id="hol-start" type="date" className="nb-input mt-2" data-testid="holiday-start-input"
                    value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} required />
                  {form.start && <p className="text-[11px] text-zinc-400 font-semibold mt-1">{fmtUK(form.start)}</p>}
                </div>
                <div>
                  <label className="nb-label" htmlFor="hol-end">Last day closed</label>
                  <input id="hol-end" type="date" className="nb-input mt-2" data-testid="holiday-end-input"
                    value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} required />
                  {form.end && <p className="text-[11px] text-zinc-400 font-semibold mt-1">{fmtUK(form.end)}</p>}
                </div>
              </div>
              <button type="submit" disabled={busy} data-testid="holiday-add-btn" className="nb-btn w-full bg-[#F9A8D4] py-2.5">
                <Plus className="w-4 h-4" /> {busy ? "Adding…" : "Add closure"}
              </button>
            </form>
          </div>

          <div className="space-y-2" data-testid="holidays-list">
            {holidays.map((h) => (
              <div key={h.id} className="nb-card p-4 flex items-center justify-between gap-3" data-testid={`holiday-row-${h.id}`}>
                <div>
                  <div className="font-bold text-sm">{h.name}</div>
                  <div className="text-xs text-zinc-500 font-semibold">
                    {new Date(h.start + "T00:00:00").toLocaleDateString("en-GB", { day: "numeric", month: "short" })} – {new Date(h.end + "T00:00:00").toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                  </div>
                </div>
                <button onClick={() => remove(h.id)} data-testid={`holiday-delete-${h.id}`}
                  className="p-2 rounded-lg border-2 border-zinc-900 bg-red-100 hover:bg-red-200 transition-colors duration-150 shrink-0">
                  <Trash2 className="w-4 h-4 text-red-700" />
                </button>
              </div>
            ))}
            {holidays.length === 0 && (
              <div className="nb-card p-6 text-center text-sm text-zinc-500" data-testid="holidays-empty">No closures added yet.</div>
            )}
          </div>

          <div className="nb-card p-6">
            <h2 className="font-display font-bold text-lg flex items-center gap-2"><GraduationCap className="w-5 h-5" /> Exams by year group</h2>
            <p className="text-xs text-zinc-500 font-semibold mt-1">Pupils see exams for their year group on their calendar. Pupils and parents can add their own too.</p>
            <form onSubmit={addExam} className="mt-4 space-y-3">
              <div>
                <label className="nb-label" htmlFor="exam-title">Exam name</label>
                <input id="exam-title" className="nb-input mt-2" data-testid="exam-title-input" placeholder="e.g. Maths mock — Hall A"
                  value={examForm.title} onChange={(e) => setExamForm({ ...examForm, title: e.target.value })} required maxLength={100} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="nb-label" htmlFor="exam-date">Date</label>
                  <input id="exam-date" type="date" className="nb-input mt-2" data-testid="exam-date-input"
                    value={examForm.date} onChange={(e) => { setExamForm({ ...examForm, date: e.target.value }); setExamPast(false); }} required />
                  {examForm.date && <p className="text-[11px] text-zinc-400 font-semibold mt-1">{fmtUK(examForm.date)}</p>}
                </div>
                <div>
                  <label className="nb-label" htmlFor="exam-time">Start time</label>
                  <input id="exam-time" type="time" className="nb-input mt-2" data-testid="exam-time-input"
                    value={examForm.start_time} onChange={(e) => setExamForm({ ...examForm, start_time: e.target.value })} />
                </div>
              </div>
              <div>
                <label className="nb-label" htmlFor="exam-year">Year group</label>
                <select id="exam-year" className="nb-input mt-2" data-testid="exam-year-select"
                  value={examForm.year_group} onChange={(e) => setExamForm({ ...examForm, year_group: e.target.value })}>
                  <option value="all">All year groups</option>
                  {yearGroups.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              <div>
                <label className="nb-label" htmlFor="exam-notes">Notes (optional)</label>
                <input id="exam-notes" className="nb-input mt-2" data-testid="exam-notes-input" placeholder="e.g. Bring calculator"
                  value={examForm.notes} onChange={(e) => setExamForm({ ...examForm, notes: e.target.value })} maxLength={200} />
              </div>
              {examPast && (
                <p className="text-xs font-bold text-amber-800 bg-amber-100 border-2 border-amber-300 rounded-lg p-2" data-testid="exam-past-warning">
                  That date is in the past — press "Add anyway" to save it (handy for recording mocks that already happened).
                </p>
              )}
              <button type="submit" data-testid="exam-add-btn" className="nb-btn w-full bg-[#C7D2FE] py-2.5">
                <GraduationCap className="w-4 h-4" /> {examPast ? "Add anyway" : "Add exam"}
              </button>
            </form>
            <div className="mt-4 space-y-2" data-testid="exams-list">
              {exams.map((e) => (
                <div key={e.id} className="rounded-xl border-2 border-zinc-900 p-3 bg-[#FDFBF7] flex items-start justify-between gap-2" data-testid={`exam-row-${e.id}`}>
                  <div className="min-w-0">
                    <div className="font-bold text-sm">{e.title}</div>
                    <div className="text-xs text-zinc-500 font-semibold">
                      {new Date(e.date + "T00:00:00").toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" })}{e.start_time ? ` · ${e.start_time}` : ""} · {e.year_group === "all" ? "All years" : e.year_group}
                    </div>
                  </div>
                  <button onClick={() => removeExam(e.id)} data-testid={`exam-delete-${e.id}`}
                    className="p-1.5 shrink-0 rounded-lg border-2 border-zinc-900 bg-red-100 hover:bg-red-200 transition-colors duration-150">
                    <Trash2 className="w-3.5 h-3.5 text-red-700" />
                  </button>
                </div>
              ))}
              {exams.length === 0 && <p className="text-sm text-zinc-400 font-semibold" data-testid="exams-empty">No exams added yet.</p>}
            </div>
          </div>
        </div>

        <div className="lg:col-span-3">
          <CalendarView holidays={holidays} exams={exams} weeks={weeks} />
        </div>
      </div>
    </div>
  );
}
